import { useState } from 'react';
import { Alert, Button, Paper, Stack, Typography } from '@mui/material';
import { useQuery } from 'react-query';
import { useTranslation } from 'react-i18next';
import { client } from '@frontend/features/common/services';
import { CandidatePicker, Candidate } from './AddSelection';
import { catalogAdminKey, SelectionPage, SelectionRow } from './queries';

/** Resolve the exact relationship, including disabled rows, before offering a write. */
export function AddDestinationHotel({
  destination,
  onChoose,
  onClose,
}: {
  destination: Candidate;
  onChoose(row: SelectionRow, mode: 'add' | 'reactivate'): void;
  onClose(): void;
}) {
  const { t } = useTranslation();
  const [hotel, setHotel] = useState<Candidate | null>(null);
  const relationship = useQuery({
    queryKey: [...catalogAdminKey, 'destination-hotel', destination.id, hotel?.id],
    queryFn: async () => {
      const { data } = await client.get<SelectionPage>('hotel-catalog-admin/hotel-memberships', {
        params: { geo_place_id: destination.id, hotel_id: hotel!.id, limit: 1, offset: 0 },
      });
      return data;
    },
    enabled: !!hotel,
    retry: false,
    staleTime: 0,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });
  const existing = relationship.data?.data[0];
  const ready = !!hotel && relationship.isSuccess && !relationship.isFetching;
  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Stack spacing={2}>
        <Typography variant="h6">{t('hotelDestinationMembership.add')}</Typography>
        <Typography variant="body2">{t('hotelDestinationMembership.catalogHelp')}</Typography>
        <CandidatePicker
          kind="hotel"
          value={hotel}
          onChange={setHotel}
          label={t('hotelDestinationMembership.hotelName')}
          searchLabel={t('hotelDestinationMembership.search')}
        />
        <Typography variant="caption">{t('hotelDestinationMembership.identityHelp')}</Typography>
        {hotel && relationship.isFetching && <Typography>{t('hotelCatalogAdmin.loading')}</Typography>}
        {hotel && relationship.isError && (
          <Alert severity="error">
            {t('hotelCatalogAdmin.loadError')}
            <Button onClick={() => relationship.refetch()}>{t('hotelCatalogAdmin.reload')}</Button>
          </Alert>
        )}
        {ready && (
          <Alert severity="info">
            {t(
              existing?.enabled
                ? 'hotelDestinationMembership.alreadyIncluded'
                : existing
                  ? 'hotelDestinationMembership.existingDisabled'
                  : 'hotelDestinationMembership.newRelationship',
              { hotel: hotel!.label, destination: destination.label },
            )}
          </Alert>
        )}
        <Stack direction="row" spacing={1}>
          <Button onClick={onClose}>{t('hotelCatalogAdmin.cancel')}</Button>
          <Button
            variant="contained"
            disabled={!ready || existing?.enabled === true}
            onClick={() => {
              if (!ready || !hotel || existing?.enabled) return;
              onChoose(
                existing ?? {
                  geo_place_id: destination.id,
                  geo_place_name: destination.label,
                  hotel_id: hotel.id,
                  hotel_display_name: hotel.label,
                  enabled: false,
                  rank: 0,
                  version: 0,
                  updated_at: '',
                },
                existing ? 'reactivate' : 'add',
              );
            }}
          >
            {t(
              existing?.enabled
                ? 'hotelDestinationMembership.included'
                : existing
                  ? 'hotelDestinationMembership.reactivate'
                  : 'hotelDestinationMembership.choose',
            )}
          </Button>
        </Stack>
      </Stack>
    </Paper>
  );
}
